import React, {ReactNode, useCallback, useEffect, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getIsSearchBoxActive} from 'modules/searchBox/selectors'
import {closeSearchBox, setIsSearchBoxActive} from 'modules/searchBox/slice'
import {AnimatePresence, motion} from 'motion/react'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {useBodyScrollLock} from '@vio/atlas/Overlay/useBodyScrollLock'
import {cssTheme} from '@vio/atlas/themes'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'

interface Props {
  /** Content to render under the shadow overlay */
  children: ReactNode
}

interface ShadowProps {
  /** The dataId applied to the shadow overlay */
  dataId: string
  /** The click handler */
  onClick: () => void
  /** The `touchmove` handler. When "active" is set to false, the event listener is destroyed. */
  onTouchMove?: () => void
  /** Whether the shadow is visible */
  isVisible: boolean
}

const ANIMATION_DURATION = 0.2

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 0;
  flex: 1 auto;
`

const ShadowElement = styled(motion.div)`
  height: 100%;
  width: 100%;
  position: fixed;
  z-index: 4;
  background: ${cssTheme.colors.background.neutral.overlay};
`

const Shadow = React.forwardRef<HTMLDivElement, ShadowProps>(
  ({dataId, isVisible, onClick, onTouchMove}, ref) => {
    useEffect(() => {
      const element =
        ref && typeof ref === 'object' && 'current' in ref ? ref.current : null
      if (onTouchMove && isVisible && element) {
        element.addEventListener('touchmove', onTouchMove)
      }

      return () => {
        if (onTouchMove && isVisible && element) {
          element.removeEventListener('touchmove', onTouchMove)
        }
      }
    }, [isVisible, onTouchMove, ref])

    return (
      <AnimatePresence>
        {isVisible && (
          <ShadowElement
            ref={ref}
            key="ShadowElement"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{
              duration: ANIMATION_DURATION
            }}
            data-id={dataId}
            onClick={onClick}
          />
        )}
      </AnimatePresence>
    )
  }
)

Shadow.displayName = 'Shadow'

const SearchBoxShadow = ({children}: Props) => {
  const dispatch = useDispatch()
  const {isMobile} = useDeviceLayout()
  const isShadowVisible = useSelector(getIsSearchBoxActive)

  const ref = useRef<HTMLDivElement>()
  useBodyScrollLock({ref, isEnabled: isShadowVisible && !isMobile})

  const closeOverlay = useCallback(() => {
    dispatch(setIsSearchBoxActive(false))
    dispatch(closeSearchBox())
  }, [dispatch])

  const handleClick = useCallback(() => {
    if (isMobile) {
      closeOverlay()
    }

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.SearchBoxShadow,
        action: Action.Clicked
      })
    )
  }, [isMobile, closeOverlay, dispatch])

  const onTouchMove = useCallback(() => {
    if (isMobile) {
      closeOverlay()
    }

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.SearchBoxShadow,
        action: Action.Scrolled
      })
    )
  }, [isMobile, closeOverlay, dispatch])

  return (
    <Wrapper>
      <Shadow
        ref={ref}
        isVisible={isShadowVisible}
        dataId="SearchBoxShadow"
        onClick={handleClick}
        onTouchMove={onTouchMove}
      />
      {children}
    </Wrapper>
  )
}

export default SearchBoxShadow
